import React from 'react';
import { Label } from 'rbx';
import swal from 'sweetalert';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import { onUpdateStripeBlockUser, useStripeBlockUser } from './helper';
import { CheckBoxVariant2, MultiTextInput } from '../../../components/elements';

const validationSchema = yup.object().shape({
  phoneNumbers: yup
    .array()
    .of(
      yup
        .string()
        .matches(
          /^\+?[0-9\s\-(). ]{7,20}$/,
          ({ value }) => `"${value}" is not a valid phone number`,
        ),
    ),
  emailIds: yup
    .array()
    .of(
      yup
        .string()
        .email(({ value }) => `"${value}" is not a valid email address`),
    ),
});

const StripeBlockUser = ({ userId, user }) => {
  const [
    stripeBlockUserLoading,
    stripeBlockUser,
    stripeBlockUserRefreshData,
  ] = useStripeBlockUser({
    stripeBlockUserId: null,
    email: user?.email,
  });

  const [userIsBlocked, setUserIsBlocked] = React.useState(false);

  const [initUserIsBlocked, setInitUserIsBlocked] = React.useState(false);
  const [userBlockFormValues, setBlockFormValues] = React.useState({});
  const [
    userBlockFormValuesIsSubmitting,
    setUserBlockFormValuesIsSubmitting,
  ] = React.useState(false);
  const [deviceIds, setDeviceIds] = React.useState([]);
  const [phoneNumbers, setPhoneNumbers] = React.useState([]);
  const [emailIds, setEmailIds] = React.useState([]);
  const [fieldErrors, setFieldErrors] = React.useState({});

  React.useEffect(() => {
    if (!stripeBlockUserLoading && stripeBlockUser?.stripe_block_user_id) {
      setUserIsBlocked(true);
    } else if (
      !stripeBlockUserLoading &&
      !stripeBlockUser?.stripe_block_user_id
    ) {
      setUserIsBlocked(false);
    } else {
      setUserIsBlocked(false);
    }

    if (!stripeBlockUserLoading) {
      setInitUserIsBlocked(true);
      setBlockFormValues({
        stripeBlockUserId: stripeBlockUser?.stripe_block_user_id || userId,
        userId,
        isUpdate: !!stripeBlockUser?.stripe_block_user_id,
        refreshData: stripeBlockUserRefreshData,
      });
      const existing = stripeBlockUser?.device_ids;
      if (existing) {
        setDeviceIds(Array.isArray(existing) ? existing : [existing]);
      }
      const existingPhones = stripeBlockUser?.phone_numbers;
      if (existingPhones) {
        setPhoneNumbers(
          Array.isArray(existingPhones) ? existingPhones : [existingPhones],
        );
      } else {
        const userPhone = user?.contact?.find(c => c.type === 'mobile')?.value;
        if (userPhone) setPhoneNumbers([userPhone]);
      }
      const existingEmails = stripeBlockUser?.email_ids;
      if (existingEmails) {
        setEmailIds(
          Array.isArray(existingEmails) ? existingEmails : [existingEmails],
        );
      } else if (user?.email) {
        setEmailIds([user.email]);
      }
    }
  }, [stripeBlockUserLoading, stripeBlockUser?.stripe_block_user_id]);

  const handleSubmit = async () => {
    setUserBlockFormValuesIsSubmitting(true);

    try {
      validationSchema.validateSync(
        { phoneNumbers, emailIds },
        { abortEarly: false },
      );
    } catch (error) {
      const errs = {};
      error.inner.forEach(e => {
        const field = e.path.split('[')[0];
        if (!errs[field]) errs[field] = e.message;
      });
      setFieldErrors(errs);
      toast.error(error.errors[0]);
      setUserBlockFormValuesIsSubmitting(false);
      return;
    }
    setFieldErrors({});

    await onUpdateStripeBlockUser({
      ...userBlockFormValues,
      isSelected: userIsBlocked,
      deviceId: deviceIds,
      phoneNumbers,
      emailIds,
    });

    setUserBlockFormValuesIsSubmitting(false);

    swal('Successfully updated user block status!');
  };

  return (
    <>
      <div className="columns m-6">
        <div className="column is-4">
          <MultiTextInput
            label="Device Ids"
            value={deviceIds}
            onChange={setDeviceIds}
            placeholder="Type a device ID and press Enter"
          />
        </div>
        <div className="column is-4">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <MultiTextInput
              label="Phone Numbers"
              value={phoneNumbers}
              onChange={v => {
                setFieldErrors(prev => ({ ...prev, phoneNumbers: '' }));
                setPhoneNumbers(v);
              }}
              placeholder="Type a phone number and press Enter"
            />
            {fieldErrors.phoneNumbers && (
              <p className="help is-danger is-size-4">
                {fieldErrors.phoneNumbers}
              </p>
            )}
          </div>
        </div>
        <div className="column is-4">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <MultiTextInput
              label="Email Ids"
              value={emailIds}
              onChange={v => {
                setFieldErrors(prev => ({ ...prev, emailIds: '' }));
                setEmailIds(v);
              }}
              placeholder="Type an email and press Enter"
            />
            {fieldErrors.emailIds && (
              <p className="help is-danger is-size-4">{fieldErrors.emailIds}</p>
            )}
          </div>
        </div>
      </div>
      <div className="columns m-6">
        <div className="column is-4">
          <div className="is-flex">
            <Label
              className="mr-4"
              style={{ fontSize: '18px', color: '#505050' }}
            >
              Block the user
            </Label>
            <div className="mr-4" style={{ marginTop: '6px' }}>
              <CheckBoxVariant2
                secondary
                label=""
                name="userIsBlocked"
                value={userIsBlocked}
                onChange={async value => {
                  const submitValues = { ...userBlockFormValues };

                  if (value) {
                    submitValues.isSelected = true;
                  } else {
                    submitValues.isSelected = false;
                  }

                  setBlockFormValues({
                    userId,
                    ...submitValues,
                    refreshData: stripeBlockUserRefreshData,
                  });

                  setUserIsBlocked(value);
                }}
                disabled={!initUserIsBlocked}
              />
            </div>
            <span
              style={{ color: '#505050', fontSize: '16px', marginTop: '2px' }}
            >
              {stripeBlockUser?.stripe_block_user_id
                ? 'Blocked User'
                : 'Active User'}
            </span>
          </div>
        </div>
      </div>
      <div className="columns m-6">
        <div className="column">
          <button
            type="button"
            className={`button is-primary${
              userBlockFormValuesIsSubmitting ? ' is-loading' : ''
            }`}
            onClick={async () => handleSubmit()}
            disabled={!initUserIsBlocked || userBlockFormValuesIsSubmitting}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
};

export default StripeBlockUser;
